import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkle, Image as ImageIcon, Download, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CreativesPage() {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("professional, photorealistic");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const load = () => api.get("/creatives").then(r => setItems(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!prompt) return toast.error("Enter a prompt");
    setLoading(true);
    try {
      await api.post("/creatives/generate", { prompt, style });
      toast.success("Image generated");
      load();
    } catch (e) {
      toast.error("Generation failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="creatives-page">
      <PageHeader
        eyebrow="Visual Content"
        title="AI-generated creatives, made for your trade."
        description="Photorealistic ad and social images, generated by Gemini Nano Banana — branded to handyman & property services."
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="sharp-card p-6 lg:col-span-1">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Describe the image</Label>
              <Textarea data-testid="creative-prompt" rows={5} placeholder="e.g. friendly handyman fixing a leaking sink, warm lighting, modern kitchen" value={prompt} onChange={(e)=>setPrompt(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Style</Label>
              <Input data-testid="creative-style" value={style} onChange={(e)=>setStyle(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <Button data-testid="btn-generate-creative" onClick={generate} disabled={loading} className="w-full bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11">
              <Sparkle className="mr-2" size={18} />{loading ? "Generating (≈10s)..." : "Generate image"}
            </Button>
            <div className="text-xs text-slate-500 leading-relaxed">
              Tip: include subject, action, lighting, location and mood for best results.
            </div>
          </div>
        </Card>
        <div className="lg:col-span-2">
          <div className="eyebrow mb-3">Recent creatives ({items.length})</div>
          {items.length === 0 ? (
            <Card className="sharp-card p-10 text-center">
              <ImageIcon size={32} className="mx-auto text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">No images yet — generate your first one.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {items.map((it) => (
                <Card key={it.id} className="sharp-card overflow-hidden group bg-white">
                  <img src={it.image_url} alt={it.prompt} className="w-full h-44 object-cover" />
                  <div className="p-3">
                    <p className="text-xs text-slate-600 line-clamp-2">{it.prompt}</p>
                    <div className="flex items-center justify-between mt-2">
                      <a href={it.image_url} download={`creative-${it.id}.png`} className="inline-flex items-center text-xs text-navy font-semibold hover:underline">
                        <Download size={12} className="mr-1" />Download
                      </a>
                      <button data-testid={`del-creative-${it.id}`} onClick={async ()=>{ if(!window.confirm("Delete this image?")) return; await api.delete(`/creatives/${it.id}`); toast.success("Deleted"); load();}} className="text-slate-400 hover:text-red-500"><Trash size={12}/></button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
